import React, { useState, useEffect } from 'react';
import { ScatterChart, Scatter, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const CorrelationPanel = () => {
  const [correlationData, setCorrelationData] = useState([]);
  const [selectedCorrelation, setSelectedCorrelation] = useState('eua-oil');
  const [heatmapData, setHeatmapData] = useState([]);

  const correlationOptions = [
    { id: 'eua-oil', label: 'EUA vs Oil Futures', x: 'EUA Price', y: 'Oil Price' },
    { id: 'eua-weather', label: 'EUA vs Weather Data', x: 'EUA Price', y: 'Temperature' },
    { id: 'oil-energy', label: 'Oil vs Energy Mix', x: 'Oil Price', y: 'Renewable %' },
    { id: 'eua-carbon', label: 'EUA vs Carbon Emissions', x: 'EUA Price', y: 'Emissions' }
  ];

  const heatmapMatrix = [
    { asset: 'EUA', eua: 1.00, oil: 0.73, gas: 0.68, coal: 0.82, weather: -0.45 },
    { asset: 'Oil', eua: 0.73, oil: 1.00, gas: 0.89, coal: 0.76, weather: -0.32 },
    { asset: 'Gas', eua: 0.68, oil: 0.89, gas: 1.00, coal: 0.71, weather: -0.28 },
    { asset: 'Coal', eua: 0.82, oil: 0.76, gas: 0.71, coal: 1.00, weather: -0.51 },
    { asset: 'Weather', eua: -0.45, oil: -0.32, gas: -0.28, coal: -0.51, weather: 1.00 }
  ];

  useEffect(() => {
    const generateScatterData = () => {
      const data = [];
      for (let i = 0; i < 50; i++) {
        const baseX = 80 + Math.random() * 20;
        const baseY = selectedCorrelation === 'eua-oil' ? 75 + Math.random() * 15 :
                     selectedCorrelation === 'eua-weather' ? 15 + Math.random() * 10 :
                     selectedCorrelation === 'oil-energy' ? 30 + Math.random() * 40 :
                     200 + Math.random() * 100;
        
        data?.push({
          x: parseFloat(baseX?.toFixed(2)),
          y: parseFloat(baseY?.toFixed(2)),
          correlation: Math.random() > 0.5 ? 'positive' : 'negative'
        });
      }
      return data;
    };

    setCorrelationData(generateScatterData());
  }, [selectedCorrelation]);

  const getCorrelationColor = (value) => {
    const absValue = Math.abs(value);
    if (absValue >= 0.8) return value > 0 ? '#059669' : '#DC2626'; // Strong correlation
    if (absValue >= 0.5) return value > 0 ? '#0EA5E9' : '#F59E0B'; // Moderate correlation
    return '#6B7280'; // Weak correlation
  };

  const currentOption = correlationOptions?.find(opt => opt?.id === selectedCorrelation);

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-semibold text-foreground">Correlation Analysis</h3>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            iconName="RefreshCw"
            iconSize={14}
          >
            Refresh
          </Button>
          <Button
            variant="outline"
            size="sm"
            iconName="Download"
            iconSize={14}
          >
            Export
          </Button>
        </div>
      </div>
      {/* Correlation Selector */}
      <div className="flex flex-wrap gap-2 mb-6">
        {correlationOptions?.map(option => (
          <Button
            key={option?.id}
            variant={selectedCorrelation === option?.id ? "default" : "outline"}
            size="sm"
            onClick={() => setSelectedCorrelation(option?.id)}
            className="text-xs"
          >
            {option?.label}
          </Button>
        ))}
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Scatter Plot */}
        <div>
          <h4 className="text-sm font-medium text-foreground mb-3">
            {currentOption?.label} Scatter Plot
          </h4>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <ScatterChart data={correlationData} margin={{ top: 20, right: 20, bottom: 20, left: 20 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                <XAxis 
                  type="number" 
                  dataKey="x" 
                  name={currentOption?.x}
                  stroke="var(--color-muted-foreground)"
                  fontSize={12}
                />
                <YAxis 
                  type="number" 
                  dataKey="y" 
                  name={currentOption?.y}
                  stroke="var(--color-muted-foreground)"
                  fontSize={12}
                />
                <Tooltip 
                  cursor={{ strokeDasharray: '3 3' }}
                  contentStyle={{
                    backgroundColor: 'var(--color-popover)',
                    border: '1px solid var(--color-border)',
                    borderRadius: '8px',
                    color: 'var(--color-popover-foreground)'
                  }}
                  formatter={(value, name) => [value, name]}
                />
                <Scatter dataKey="y" fill="var(--color-primary)">
                  {correlationData?.map((entry, index) => (
                    <Cell 
                      key={`cell-${index}`} 
                      fill={entry?.correlation === 'positive' ? 'var(--color-success)' : 'var(--color-warning)'} 
                    />
                  ))}
                </Scatter>
              </ScatterChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Correlation Heatmap */}
        <div>
          <h4 className="text-sm font-medium text-foreground mb-3">Correlation Matrix</h4>
          <div className="bg-muted rounded-lg p-4">
            <div className="grid grid-cols-6 gap-1 text-xs">
              {/* Header row */}
              <div></div>
              {['EUA', 'Oil', 'Gas', 'Coal', 'Weather']?.map(asset => (
                <div key={asset} className="text-center font-medium text-muted-foreground p-1">
                  {asset}
                </div>
              ))}
              
              {/* Data rows */}
              {heatmapMatrix?.map(row => (
                <React.Fragment key={row?.asset}>
                  <div className="font-medium text-muted-foreground p-1">{row?.asset}</div>
                  {Object.entries(row)?.slice(1)?.map(([key, value]) => (
                    <div
                      key={key}
                      className="text-center p-1 rounded text-white text-xs font-medium"
                      style={{ backgroundColor: getCorrelationColor(value) }}
                    >
                      {value?.toFixed(2)}
                    </div>
                  ))}
                </React.Fragment>
              ))}
            </div>
          </div>
        </div>
      </div>
      {/* Correlation Insights */}
      <div className="mt-6 p-4 bg-muted rounded-lg">
        <div className="flex items-start gap-3">
          <Icon name="TrendingUp" size={20} className="text-primary mt-1" />
          <div>
            <h5 className="font-medium text-foreground mb-2">Key Correlations</h5>
            <div className="space-y-2 text-sm text-muted-foreground">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 bg-success rounded-full"></div>
                <span>Strong positive correlation (0.82) between EUA and Coal prices</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 bg-primary rounded-full"></div>
                <span>Moderate positive correlation (0.73) between EUA and Oil futures</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 bg-warning rounded-full"></div>
                <span>Negative correlation (-0.45) between EUA and Weather patterns</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CorrelationPanel;